<script setup lang="ts">
/**
 * WL Engine Dispatcher — replaces `WhiteLabelLayout` at the WL host boundary.
 *
 * Flow:
 *   1. Read the shop's active pack engine id from `useBrandStore().activeEngineId`
 *   2. Lazy-load the engine module via the registry
 *   3. Mount `engine.Chrome` if provided; otherwise the fallback path mounts
 *      the existing `WhiteLabelLayout.vue` (which IS the `default` engine's
 *      chrome)
 *   4. For pack-aware routes, mount the engine's page component from
 *      `engine.pages` in place of the default view. Every other route
 *      renders via `<router-view />` inside the engine's chrome — pack-
 *      shared surfaces that use --wl-* tokens for skinning.
 *
 * Backwards compat: if `activeEngineId === 'default'` (no pack, or a pack
 * that picks the default engine) the dispatcher is a byte-identical wrapper
 * around the existing `WhiteLabelLayout`.
 *
 * Failure mode: if an engine load throws, we fall back to the default WL
 * layout so the site stays up.
 */
import { computed, ref, shallowRef, watch, type Component } from 'vue'
import { useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useBrandStore } from '@/stores/brand'
import { loadEngine } from '@/wl-engines/registry'
import WhiteLabelLayout from '@/layouts/WhiteLabelLayout.vue'

const store = useBrandStore()
const { activeEngineId } = storeToRefs(store)
const route = useRoute()

const ChromeComp        = shallowRef<Component | null>(null)
const HomePage          = shallowRef<Component | null>(null)
const ProductsPage      = shallowRef<Component | null>(null)
const CategoryPage      = shallowRef<Component | null>(null)
const CartPage          = shallowRef<Component | null>(null)
const CheckoutPage      = shallowRef<Component | null>(null)
const ConfirmationPage  = shallowRef<Component | null>(null)
const AuthPage          = shallowRef<Component | null>(null)
const AccountPage       = shallowRef<Component | null>(null)
const TermsPage         = shallowRef<Component | null>(null)
const PrivacyPage       = shallowRef<Component | null>(null)
const SitemapPage       = shallowRef<Component | null>(null)
const AboutPage         = shallowRef<Component | null>(null)
const engineLoadFailed  = ref(false)

watch(
  () => activeEngineId.value,
  async (id) => {
    engineLoadFailed.value = false
    if (!id || id === 'default') {
      ChromeComp.value = null
      HomePage.value = null
      ProductsPage.value = null
      CategoryPage.value = null
      CartPage.value = null
      CheckoutPage.value = null
      ConfirmationPage.value = null
      AuthPage.value = null
      AccountPage.value = null
      SitemapPage.value = null
      AboutPage.value = null
      TermsPage.value = null
      PrivacyPage.value = null
      return
    }
    try {
      const engine = await loadEngine(id)
      // Every slot is a LOADER (() => Promise<Component>) — call + await
      // each one so `<component :is>` receives a Component, not a raw
      // function. (Not awaiting Chrome was the bug that made pack pages
      // render as [object Promise] / blank chrome.)
      ChromeComp.value       = engine.Chrome              ? await engine.Chrome()              : null
      HomePage.value         = engine.pages.home          ? await engine.pages.home()          : null
      ProductsPage.value     = engine.pages.products      ? await engine.pages.products()      : null
      CategoryPage.value     = engine.pages.category      ? await engine.pages.category()      : null
      CartPage.value         = engine.pages.cart          ? await engine.pages.cart()          : null
      CheckoutPage.value     = engine.pages.checkout      ? await engine.pages.checkout()      : null
      ConfirmationPage.value = engine.pages.confirmation  ? await engine.pages.confirmation()  : null
      AuthPage.value         = engine.pages.auth          ? await engine.pages.auth()          : null
      AccountPage.value      = engine.pages.account       ? await engine.pages.account()       : null
      SitemapPage.value      = engine.pages.sitemap       ? await engine.pages.sitemap()       : null
      AboutPage.value        = engine.pages.about         ? await engine.pages.about()         : null
      TermsPage.value        = (engine.pages as any).terms   ? await (engine.pages as any).terms()   : null
      PrivacyPage.value      = (engine.pages as any).privacy ? await (engine.pages as any).privacy() : null
    } catch (err) {
      console.error(`WL engine "${id}" load failed — falling back to default`, err)
      engineLoadFailed.value = true
      ChromeComp.value = null
      HomePage.value = null
      ProductsPage.value = null
      CategoryPage.value = null
      CartPage.value = null
      CheckoutPage.value = null
      ConfirmationPage.value = null
      AuthPage.value = null
      AccountPage.value = null
      SitemapPage.value = null
      AboutPage.value = null
      TermsPage.value = null
      PrivacyPage.value = null
    }
  },
  { immediate: true },
)

// Path → pack-page mapping. Any URL not matched here uses `<router-view />`
// inside pack chrome (shared skinned surfaces).
const activePagePath = computed(() => {
  const p = route.path
  if (p === '/' || p === '') return 'home'
  // Legacy `/storefront` (bare) also maps to the pack Home. `/storefront/checkout`
  // stays uncaught so the payment resume path continues to reach canonical.
  if (p === '/storefront' || p === '/storefront/') return 'home'
  // /products AND /products/<slug> both go to the pack's products page —
  // the page reads the slug from route.params to filter down. There is no
  // separate category page; the same layout with a tab pre-selected covers it.
  // Legacy `/storefront/products*` paths also route here so any old link on
  // the WL host lands on the pack UI, not the canonical Zaveato ShopProductsView.
  if (p === '/products' || p.startsWith('/products/')) return 'products'
  if (p === '/storefront/products' || p.startsWith('/storefront/products/')) return 'products'
  if (p === '/cart') return 'cart'
  if (p === '/checkout' || p.startsWith('/checkout/details') || p.startsWith('/checkout/payment')) return 'checkout'
  if (p.startsWith('/checkout/confirmation') || p.startsWith('/order-confirmation') || p.startsWith('/confirmation') || p.startsWith('/orders/confirmation')) return 'confirmation'
  if (p.startsWith('/auth/')) return 'auth'
  if (p === '/account' || p.startsWith('/account/')) return 'account'
  if (p === '/sitemap') return 'sitemap'
  if (p === '/about' || p === '/about-us') return 'about'
  // WL-scoped legal pages — pack Terms.vue reads shop's own Terms/Returns/
  // Refunds/Cancellation via getShopTerms; pack Privacy.vue renders a
  // GDPR statement with shop + Zaveato platform attribution. Both are
  // indexable + self-canonical to the WL domain (per the rewriter change).
  if (p === '/terms-and-conditions' || p === '/terms') return 'terms'
  if (p === '/privacy-policy' || p === '/privacy') return 'privacy'
  return null
})

const ActivePage = computed<Component | null>(() => {
  switch (activePagePath.value) {
    case 'home':         return HomePage.value
    case 'products':     return ProductsPage.value
    case 'category':     return CategoryPage.value
    case 'cart':         return CartPage.value
    case 'checkout':     return CheckoutPage.value
    case 'confirmation': return ConfirmationPage.value
    case 'auth':         return AuthPage.value
    case 'account':      return AccountPage.value
    case 'sitemap':      return SitemapPage.value
    case 'about':        return AboutPage.value
    case 'terms':        return TermsPage.value
    case 'privacy':      return PrivacyPage.value
    default: return null
  }
})

const useDefault = computed(() => !ChromeComp.value || engineLoadFailed.value)
</script>

<template>
  <!-- Default engine (or engine load failed) — original inline layout. -->
  <WhiteLabelLayout v-if="useDefault">
    <router-view />
  </WhiteLabelLayout>

  <!-- Pack-engine chrome. Pages that the engine ships override the
       router-view; everything else falls through to the standard route
       component skinned via --wl-* tokens. -->
  <component v-else :is="ChromeComp!">
    <component v-if="ActivePage" :is="ActivePage" />
    <router-view v-else />
  </component>
</template>
